import { BerthType } from '@/constant';

export enum PriceUnit {
  PER_DAY = 'PER_DAY',
  PER_WEEK = 'PER_WEEK',
  PER_HOUR = 'PER_HOUR',
}

export enum BerthStatus {
  AVAILABLE = 1,
  NOT_AVAILABLE = 2,
  OCCUPIED = 3,
  MAINTENANCE = 4,
}

export interface IBerthData {
  id: string;
  berth_id?: string;
  berth_number?: string;
  harbor_id?: string;
  category_id?: string;
  size_category?: string;
  length?: number;
  width?: number;
  depth?: number;
  depth_unit?: string;
  grouping?: string;
  amenities?: string[];
  price?: number;
  price_unit?: PriceUnit;
  status?: number;
  created_at?: string;
  featured?: boolean;
  harbor?: { id?: string; name?: string; location?: string };
  media?: string[];
  quantity?: number;
  count?: number;
  pier_prefix?: string;
  pier_id?: string;
  pier?: { id: string; name: string } | null | undefined;
  mooring_style?: string;
  start_date?: string;
  end_date?: string;
  unavailabilities?: Unavailability[];
  berth_type?: number;
  seasonal_pricing?: Partial<Record<string, number>>;
}

export interface UnavailabilityResponse {
  blocked: boolean;
  conflictingBookings: boolean;
}

export enum UnavailabilityType {
  BLOCKED = 1,
  EXTERNALLY_RESERVED = 2,
}

export interface UnavailabilityUser {
  id: string;
  name?: string | null;
  email?: string;
}

export interface ExternalReservationResponse {
  reserved: boolean;
  unavailabilityId: string;
}

export interface CreateExternalReservationPayload {
  id: string;
  startDate?: string;
  endDate?: string;
  reason: string;
  externalReference?: string;
}

export interface UpdateExternalReservationPayload {
  id: string;
  unavailabilityId: string;
  startDate?: string;
  endDate?: string;
  reason?: string;
  externalReference?: string;
}

export interface ReleaseExternalReservationPayload {
  id: string;
  unavailabilityId: string;
}

export interface Berth {
  id: string;
  harbor_id: string;
  category_id: string;
  berth_number: string;
  amenities: string[];
  price?: number;
  price_unit?: PriceUnit;
  status: number;
  created_at: string;
  featured?: boolean;
  harbor: { id?: string; name?: string; location?: string };
}

export interface BerthResponse {
  id: string;
  category_id: string;
  berth_number: string;
  amenities: string[];
  price?: number;
  price_unit?: PriceUnit;
  status: number;
  created_at: string;
  updated_at?: string;
  featured?: boolean;
  harbor: {
    id: string;
    name: string;
    description?: string;
    logo?: string | null;
    location: string;
    latitude?: string;
    longitude?: string;
    berth_capacity?: string;
    depth?: number | null;
    max_depth?: string;
    min_depth?: string;
    depth_unit?: string | null;
    contact_phone?: string;
    contact_email?: string;
    category?: string;
    business_hours?: string | null;
    facilities?: string[];
    amenities?: string[];
    mooring_info?: string | null;
    media?: string[];
    website_url?: string;
    is_deleted?: boolean;
    status?: number;
    featured?: boolean;
    country_code?: string;
    country_short_code?: string;
    fairway_map?: string | null;
    vhf_channel?: string;
    minimum_advance_booking_days?: number;
    club_id?: string | null;
    send_booking_to_admin?: boolean;
    whatsapp_link?: string | null;
    facebook_link?: string | null;
    instagram_link?: string | null;
    pdf_link?: string | null;
    admin_notes?: string | null;
    history_log?: string | null;
    primary_language_spoken?: string | null;
    mooring_style?: string | null;
    coastal_area?: string | null;
    created_at?: string;
    updated_at?: string;
  };
  pier_id?: string | null;
  pier?: {
    id: string;
    name: string;
    harbor_id?: string;
    is_deleted?: boolean;
    boater_label?: string | null;
    capacity_mode?: string;
    pooled_capacity?: number | null;
  } | null;
  mooring_style?: string;
  start_date?: string;
  end_date?: string;
  unavailabilities?: Unavailability[];
  berth_count?: number;
  pier_prefix?: string | null;
  group_id?: string | null;
  is_deleted?: boolean;
  category?: {
    id: string;
    name: string;
    description?: string;
    min_length?: number;
    max_length?: number | null;
    min_width?: number;
    max_width?: number | null;
    min_depth?: number;
    max_depth?: number | null;
    is_deleted?: boolean;
    status?: number;
    created_at?: string;
    updated_at?: string;
  };
  berth_type?: BerthType;
  seasonal_pricing?: Partial<Record<string, number>>;
}

export interface Unavailability {
  start_date: string;
  end_date: string;
  reason?: string;
  id?: string;
  type?: UnavailabilityType;
  external_reference?: string | null;
  created_by?: UnavailabilityUser | null;
  updated_by?: UnavailabilityUser | null;
  updated_at?: string | null;
}

export interface ByIdResponse {
  data: BerthResponse;
}

export interface BerthListQueryParams {
  page?: number;
  limit?: number;
  search?: string;
  is_featured?: boolean;
  harbor_id?: string;
}

export interface FeaturedBerthResponse {
  id: string;
  harbor_id: string;
  category_id: string;
  amenities: string[];
  price?: number;
  price_unit?: PriceUnit;
  status: number;
  media: string[];
  featured: boolean;
  created_at: string;
  updated_at: string;
  harbor: Record<string, unknown>;
}

export interface MediaPayload {
  id?: string;
  media: File[];
}

export interface UpdateFeaturedPayload {
  id: string;
  featured: boolean;
}

export interface CreateBerthGroupPayload {
  name: string;
  harbor_id: string;
}

export interface Mooring {
  id: string | number;
  name: string;
  description: string;
  label: string;
}

export interface MooringResponse {
  data: Mooring[];
}
